import { Source } from "../types/chat";
import { ExternalLink, FileText } from "lucide-react";
import { useConfig } from "hooks/use-config";
import { getFile } from "services/storage-service";
import { useToast } from "hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useEffect, useRef, useState } from "react";
import PDFViewer from "features/file-manager/components/pdf-viewer";
import { extensionToFileType } from "features/file-manager/utils/file-manager";

interface SourceFileProps {
    sourceFile: Source;
}

export default function SourceFile({ sourceFile }: SourceFileProps) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTextOverflowing, setIsTextOverflowing] = useState(false);
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [fileType, setFileType] = useState('');

    useEffect(() => {
        if (fileType == "") {
            const extension = sourceFile.fileName.split('.').pop() || '';
            setFileType(extensionToFileType(extension));
        }
    }, [sourceFile.fileName])


    const openFile = async (fileId: string) => {
        if (url || loading) {
            return;
        }
        setLoading(true);
        try {
            const response = await getFile(fileId, configurationSettings, undefined);
            if (response.isSuccess && response.url) {
                if (fileType === 'Pdf' || fileType === 'Photo') {
                    setUrl(response.url);
                } else {
                    window.open(response.url, '_blank');
                }

            }
        } catch (error) {
            console.error('Failed to open file:', error);
            toast({
                variant: 'destructive',
                title: t('ERROR'),
                description: t('FAILED_TO_OPEN_FILE'),
            });
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current) {
                const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
                setIsTextOverflowing(isOverflowing);
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);

        return () => {
            window.removeEventListener('resize', checkOverflow);
        };
    }, []);

    return (<>

        <button
            key={sourceFile.fileId}
            className="flex h-[18px] items-center justify-between bg-gray-200 gap-1 p-2 rounded-full cursor-pointer"
            onClick={() => openFile(sourceFile.fileId)}
            disabled={loading}
        >
            <FileText className="h-[10px] w-[10px] text-gray-500" />
            {isTextOverflowing ? (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span ref={textRef} className="text-[8px] text-black truncate max-w-[100px]">
                                {sourceFile.fileName}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-xs text-gray-50 max-w-[200px] truncate">{sourceFile.fileName}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                <span ref={textRef} className="text-[8px] text-black truncate max-w-[100px]">
                    {sourceFile.fileName}
                </span>
            )}
            <ExternalLink className="h-[10px] w-[10px] text-gray-500" />
        </button>
        {url && (
            <PDFViewer url={url} setUrl={setUrl} fileType={fileType} />
        )}
    </>);
}